import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Alert,
  Button,
  ConfirmDialog,
  GlassCard,
  StatTile,
} from '@jumpstone/ui-react';
import type { MigrationRunReport } from '../../../data/use_wagepro_migration';

/**
 * Step 7 — Done: import summary, a link to the next pay run, and an Undo guarded
 * by a confirmation dialog (undo voids the imported runs, reverses the opening
 * leave and deactivates the created employees — only while that is still safe).
 * JSX + the confirm-open local state only; the undo call lives in the screen.
 */
export function MigrationDoneStep({
  report,
  canUndo,
  undoing,
  undoError,
  onUndo,
  onGoToPayroll,
  onDone,
}: {
  report: MigrationRunReport | null;
  canUndo: boolean;
  undoing: boolean;
  undoError: string | null;
  onUndo: () => void;
  onGoToPayroll: () => void;
  onDone: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <GlassCard className="rounded-2xl p-5">
      <div className="flex flex-col gap-5">
        <div>
          <h3 className="text-base font-semibold">
            {t('migration.done.title')}
          </h3>
          <p className="mt-1 text-sm text-[color:var(--ink-muted)]">
            {t('migration.done.body')}
          </p>
        </div>

        {report && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <StatTile
              kicker={t('migration.done.loaded')}
              value={String(report.loaded)}
            />
            <StatTile
              kicker={t('migration.done.total')}
              value={String(report.total)}
            />
          </div>
        )}

        {undoError !== null && (
          <Alert variant="danger" title={undoError}>
            {undoError}
          </Alert>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button onClick={onGoToPayroll}>
            {t('migration.done.next_pay_run')}
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onDone}>
              {t('migration.done.finish')}
            </Button>
            <Button
              variant="destructive"
              disabled={!canUndo || undoing}
              onClick={() => {
                setConfirmOpen(true);
              }}
            >
              {t('migration.done.undo')}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('migration.undo.confirm_title')}
        description={t('migration.undo.confirm_body')}
        confirmLabel={t('migration.done.undo')}
        cancelLabel={t('migration.button.cancel')}
        variant="destructive"
        onConfirm={() => {
          setConfirmOpen(false);
          onUndo();
        }}
      />
    </GlassCard>
  );
}
